import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Loader2, Check, ChevronRight, ChevronDown, Paperclip, X } from 'lucide-react';
import { streamChat } from './sseClient';
import LinkPreview from './LinkPreview';
import SharedFile from './SharedFile';
import PdfButton from '../chat/PdfButton';
import { AGENT_NAME, API_BASE_URL } from '../../config';
import { getSessionMessages, mapHermesMessages, extractToolMeta, redactSecrets } from '../../lib/hermesChats';
import { extractPdfCitations, isPdfUrl } from '../../lib/pdfCitations';

// URLs in assistant text → clickable + unfurled preview cards. Trailing
// punctuation is trimmed so "…/DELL." doesn't include the period.
const URL_RE = /https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"]/g;

// Files the agent saved to ~/shared and mentioned by path (Hermes "Deliverable Mode").
// We map `~/shared/<rel>` (or `/home/<user>/shared/<rel>`, `/root/shared/<rel>`) →
// `<filesBase>/<rel>` and render a preview/download card. doc 148.
const SHARED_RE = /(?:~|\/home\/[^/\s]+|\/root)\/shared\/([^\s`)<>"'*]+)/g;
const FILE_EXT_OK = /\.(png|jpe?g|gif|webp|svg|bmp|ico|pdf|csv|tsv|txt|md|json|log|ya?ml|mp3|wav|ogg|m4a|mp4|mov|webm|m4v|xlsx?|docx?|zip|tar|gz)$/i;

// Drop FENCED code blocks so we don't render a path buried in a multi-line code
// sample. We intentionally KEEP inline code: models like Gemini wrap their
// deliverable paths in inline code (`~/shared/chart.png`), and stripping inline
// code hid every chart/file preview after the Azure→Gemini cutover (plan 211).
// SHARED_RE's char class already excludes backticks, so it stops cleanly at the
// closing `. doc 148.
function stripCode(s) {
  return s.replace(/```[\s\S]*?```/g, ' ');
}

// ——— File uploads (plan 206 Part B) ——————————————————————————————————————
// Client-side guardrails mirror the broker; the server re-validates authoritatively.
const UPLOAD_ACCEPT = '.pdf,.xlsx,.xls,.csv,.docx,.pptx,.png,.jpg,.jpeg';
const UPLOAD_ALLOWED_EXT = ['pdf', 'xlsx', 'xls', 'csv', 'docx', 'pptx', 'png', 'jpg', 'jpeg'];
const UPLOAD_MAX_FILE = 25 * 1024 * 1024;
const UPLOAD_MAX_COUNT = 5;

// POST the files to the Django broker, which lands them in the user's sandbox
// ~/uploads and returns their paths. Same auth as the session broker (JWT bearer).
async function uploadFiles(files) {
  const token = localStorage.getItem('token');
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  const res = await fetch(`${API_BASE_URL}/api/agent/hermes/upload/`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd, // no Content-Type — the browser sets the multipart boundary
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.detail || body.error || `HTTP ${res.status}`);
  return body;
}

// ——— Wait-state UX (doc 179, Tier 0) ———————————————————————————————
// No SSE frame of ANY kind for this long → the turn is presumed stalled (the
// known worker SSE stall) and the status flips to "Stop and resend".
// window override exists so tests can trip it without waiting 45s.
const STALL_MS = () => Number(window.__hermesStallMs) || 45_000;
const WAKE_HINT_MS = 2_500;  // streaming but zero frames yet → "Waking…" copy
const LONG_STEP_MS = 5_000;  // running step gains "(still running · Ns)"

const TOOL_VERBS = {
  terminal: 'Running command',
  skill_view: 'Reading skill',
  skills_list: 'Listing skills',
  read_file: 'Reading file',
  write_file: 'Writing file',
  edit_file: 'Editing file',
  list_dir: 'Listing files',
  search_files: 'Searching files',
  web_search: 'Searching the web',
  web_fetch: 'Fetching page',
  todo_write: 'Updating plan',
};
function toolVerb(tool) {
  if (!tool) return 'Working';
  if (TOOL_VERBS[tool]) return TOOL_VERBS[tool];
  if (tool.startsWith('browser')) return 'Browsing';
  const t = tool.replace(/[_-]+/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function fmtClock(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
// Client-side step timing includes network jitter — render rounded ("~1.2s"),
// never as a precise measurement (Tier 2's duration_s is the authoritative one).
function fmtDur(ms) {
  if (ms < 950) return '<1s';
  const s = ms / 1000;
  if (s < 10) return `~${s.toFixed(1)}s`;
  if (s < 60) return `~${Math.round(s)}s`;
  return `~${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}

// Display-only branding: the underlying agent framework is "Hermes"; users should
// only ever see the product name "AgentBro". Replace the term wherever it surfaces
// in rendered chat-stream text (commands, tool output, prose). Case-aware. ~free:
// one string scan at render. IMPORTANT: apply ONLY to text the user reads — never
// to URL/path resolution (filesBase `hermes-proxy…/f/`, `/hermesfiles/`, `~/.hermes/`),
// or links/images that legitimately contain "hermes" would stop resolving.
function brand(s) {
  if (typeof s !== 'string' || !s) return s;
  return s.replace(/hermes/gi, (m) =>
    m === m.toUpperCase() ? 'AGENTBRO'
      : m[0] === m[0].toUpperCase() ? 'AgentBro'
      : 'agentbro'
  );
}

// One trail row. Tier 1: when the step carries a result, a "→ preview" line
// appears under it; clicking the row toggles the (ingest-capped) raw output.
// `thinkMs` = the gap before this step's tool ran (model reasoning+generation
// time, client-side ≈ incl. network) — surfaced so the otherwise-invisible
// thinking time is visible, not just tool exec time.
function StepRow({ a, now, thinkMs }) {
  const [open, setOpen] = useState(false);
  const runningFor = a.status !== 'completed' && now && a.startedAt ? now - a.startedAt : 0;
  const hasResult = !!a.result?.text;
  return (
    <div className="text-[11px] text-gray-400">
      <div
        className={`flex items-center gap-1.5 ${hasResult ? 'cursor-pointer hover:text-gray-200' : ''}`}
        onClick={hasResult ? () => setOpen((o) => !o) : undefined}
      >
        <span className="w-3 inline-flex justify-center shrink-0">
          {a.status === 'completed'
            ? <Check size={11} className="text-green-500" />
            : <Loader2 size={11} className="animate-spin text-blue-400" />}
        </span>
        {thinkMs > 1500 && (
          <span className="text-purple-400/60 shrink-0 italic" title="model thinking time before this step">
            💭{fmtDur(thinkMs)}
          </span>
        )}
        {a.emoji && <span>{a.emoji}</span>}
        <span className="text-gray-300 shrink-0" title={a.tool}>{brand(toolVerb(a.tool))}</span>
        {a.label && <span className="text-gray-500 truncate max-w-[340px] font-mono">{brand(a.label)}</span>}
        {a.status === 'completed' && a.durationMs != null && (
          <span className="text-gray-600 shrink-0">{fmtDur(a.durationMs)}</span>
        )}
        {runningFor > LONG_STEP_MS && (
          <span className="text-gray-500 shrink-0 italic">still running · {Math.floor(runningFor / 1000)}s</span>
        )}
        {hasResult && (open
          ? <ChevronDown size={11} className="shrink-0 text-gray-600" />
          : <ChevronRight size={11} className="shrink-0 text-gray-600" />)}
      </div>
      {hasResult && a.result.preview && !open && (
        <div className="pl-[18px] text-gray-600 truncate max-w-[420px]">→ {brand(a.result.preview)}</div>
      )}
      {hasResult && open && (
        <pre className="ml-[18px] mt-0.5 mb-1 p-1.5 bg-black/30 rounded text-[10px] text-gray-400 whitespace-pre-wrap break-all max-h-48 overflow-y-auto">
          {brand(a.result.text)}{a.result.truncated ? `\n… (truncated, ${(a.result.bytes / 1024).toFixed(1)} KB total)` : ''}
        </pre>
      )}
    </div>
  );
}

function StepList({ items, now }) {
  // Thinking time before each step = this step's startedAt minus the previous
  // step's end (startedAt + durationMs). It's the window the model spent
  // reasoning + generating the next tool call — invisible until now.
  let prevEnd = null;
  return (
    <div className="space-y-0.5 mt-1 border-l border-gray-700/50 pl-2">
      {items.map((a) => {
        const thinkMs = prevEnd != null && a.startedAt ? a.startedAt - prevEnd : 0;
        if (a.startedAt) prevEnd = a.startedAt + (a.durationMs || 0);
        return <StepRow key={a.id} a={a} now={now} thinkMs={thinkMs} />;
      })}
    </div>
  );
}

// Live while the agent works; collapses to a one-line summary once the answer
// arrives (expandable to review what it did).
function ActivityTrail({ items, done, now }) {
  const [open, setOpen] = useState(!done);
  const prevDone = useRef(done);
  useEffect(() => {
    if (done && !prevDone.current) setOpen(false);
    prevDone.current = done;
  }, [done]);

  if (!done) return <div className="mb-1.5"><StepList items={items} now={now} /></div>;
  return (
    <div className="mb-1.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-[11px] text-gray-500 hover:text-gray-300 inline-flex items-center gap-1"
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        ran {items.length} step{items.length !== 1 ? 's' : ''}
      </button>
      {open && <StepList items={items} now={now} />}
    </div>
  );
}

// One always-legible "what is it doing right now" line for the streaming turn:
// stall warning > current running tool > waking/thinking placeholder. The
// elapsed clock ticks from the moment the user hit Send (T0.1).
function StatusLine({ activity, hasContent, now, turnStartedAt, firstFrameSeen, stalled }) {
  const elapsed = turnStartedAt && now ? now - turnStartedAt : 0;
  const clock = (
    <span className="text-gray-600 tabular-nums shrink-0 ml-auto pl-2">{fmtClock(elapsed)}</span>
  );
  if (stalled) {
    return (
      <div className="text-[12px] text-amber-400 flex items-center gap-1.5 mt-1.5">
        <Loader2 size={12} className="animate-spin shrink-0" />
        <span>This is taking longer than usual — you can Stop and resend.</span>
        {clock}
      </div>
    );
  }
  const running = (activity || []).filter((a) => a.status !== 'completed');
  const current = running[running.length - 1];
  if (current) {
    const runningFor = current.startedAt && now ? now - current.startedAt : 0;
    return (
      <div className="text-[12px] text-gray-400 flex items-center gap-1.5 mt-1.5">
        <Loader2 size={12} className="animate-spin text-blue-400 shrink-0" />
        {current.emoji && <span className="shrink-0">{current.emoji}</span>}
        <span className="text-gray-200 shrink-0" title={current.tool}>{toolVerb(current.tool)}</span>
        {current.label && <span className="text-gray-500 truncate max-w-[280px]">{current.label}</span>}
        {runningFor > LONG_STEP_MS && (
          <span className="text-gray-500 shrink-0 italic">still running · {Math.floor(runningFor / 1000)}s</span>
        )}
        {clock}
      </div>
    );
  }
  if (hasContent) return null; // text is visibly growing — that's its own liveness
  const waking = !firstFrameSeen && elapsed > WAKE_HINT_MS;
  return (
    <div className="text-[12px] text-gray-500 flex items-center gap-1.5 mt-1.5">
      <Loader2 size={12} className="animate-spin shrink-0" />
      <span>{waking ? `Waking ${AGENT_NAME}…` : 'Thinking…'}</span>
      {clock}
    </div>
  );
}

function MarkdownBody({ text }) {
  return (
    <div className="leading-relaxed [&_p]:my-1 [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4 [&_li]:my-0.5 [&_a]:text-blue-400 [&_a]:underline [&_code]:bg-black/30 [&_code]:px-1 [&_code]:rounded">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{ a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" /> }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

function LinkPreviews({ text, filesBase }) {
  let urls = Array.from(new Set(text.match(URL_RE) || []));
  // Shared-file URLs (if the agent emits a full one) are rendered by SharedFiles, not unfurled.
  if (filesBase) urls = urls.filter((u) => !u.startsWith(filesBase));
  // PDF URLs render as citation chips (PdfCitations → side viewer), not generic cards.
  urls = urls.filter((u) => !isPdfUrl(u));
  if (!urls.length) return null;
  return <>{urls.slice(0, 5).map((u) => <LinkPreview key={u} url={u} />)}</>;
}

// PDF citations the agent referenced — corpus-search r2_urls, or the legacy
// [SOURCE, Title, Page, url] form. Rendered as clickable chips that open the
// source PDF at the cited page in the side viewer (plan 206 Part A). Inert if no
// onOpenPdf handler is wired (e.g. the chart-sidebar Hermes tab).
function PdfCitations({ citations, onOpen }) {
  if (!citations?.length || !onOpen) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1">
      <span className="text-[10px] uppercase tracking-wider text-gray-500 mr-0.5">sources</span>
      {citations.slice(0, 8).map((c) => (
        <PdfButton key={`${c.url}|${c.page}`} onClick={() => onOpen(c.url, c.page, c.title)}>
          {brand(c.title)}{c.page > 1 ? `, p.${c.page}` : ''}
        </PdfButton>
      ))}
    </div>
  );
}

// Detect ~/shared paths (and full filesBase URLs) the agent mentioned, map each to a
// worker /f/ URL, and render a typed preview/download card. Skips code spans + applies
// an extension allowlist (Hermes Deliverable-Mode rules). doc 148.
function SharedFiles({ text, filesBase }) {
  if (!filesBase) return null;
  const clean = stripCode(text);
  const found = new Map(); // rel → {url, name}
  const base = filesBase.replace(/\/$/, '');
  let m;
  SHARED_RE.lastIndex = 0;
  while ((m = SHARED_RE.exec(clean)) !== null) {
    let rel = m[1].replace(/[.,;:!?'")\]]+$/, ''); // trim trailing punctuation
    if (!rel || !FILE_EXT_OK.test(rel) || found.has(rel)) continue;
    found.set(rel, { url: `${base}/${rel.split('/').map(encodeURIComponent).join('/')}`, name: rel.split('/').pop() });
  }
  // Also catch full filesBase-prefixed URLs the agent might print directly.
  for (const u of text.match(URL_RE) || []) {
    if (!u.startsWith(base + '/')) continue;
    const rel = u.slice(base.length + 1);
    if (!rel || !FILE_EXT_OK.test(rel) || found.has(rel)) continue;
    found.set(rel, { url: u, name: rel.split('/').pop() });
  }
  const items = [...found.values()].slice(0, 5);
  if (!items.length) return null;
  return <>{items.map((f) => <SharedFile key={f.url} url={f.url} name={f.name} />)}</>;
}

// Thin OpenAI-SSE chat client for Hermes. Reusable in /hermes (standalone) and
// the chart-sidebar Hermes tab.
export default function HermesChat({
  baseUrl,
  apiKey,
  conversation,    // optional Hermes conversation name (thread)
  sessionKey,      // optional X-Hermes-Session-Key (per-user long-term memory)
  filesBase,       // optional base URL for ~/shared deliverables (doc 148)
  seedContext,     // optional system message prepended to every turn
  placeholder = `Ask ${AGENT_NAME} anything…`,
  onUserMessage,   // optional callback(text) fired when the user sends a message
  onSessionStale,  // optional async () => Promise<void> — invoked when the upstream
                   // reports the sandbox is gone; should refresh the session.
  onOpenPdf,       // optional (url, page, title) — opens a PDF citation in the side viewer
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [statusHint, setStatusHint] = useState(null);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  // File-upload composer state (plan 206 Part B).
  const [pending, setPending] = useState([]);   // File[] queued for the next send
  const [uploadErr, setUploadErr] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;
    setUploadErr(null);
    setPending((prev) => {
      const next = [...prev];
      for (const f of incoming) {
        const ext = (f.name.split('.').pop() || '').toLowerCase();
        if (!UPLOAD_ALLOWED_EXT.includes(ext)) { setUploadErr(`Unsupported file: ${f.name}`); continue; }
        if (f.size > UPLOAD_MAX_FILE) { setUploadErr(`Too large (max 25 MB): ${f.name}`); continue; }
        if (next.length >= UPLOAD_MAX_COUNT) { setUploadErr(`Max ${UPLOAD_MAX_COUNT} files per message`); break; }
        if (next.some((p) => p.name === f.name && p.size === f.size)) continue; // dedupe
        next.push(f);
      }
      return next;
    });
  };
  const removeFile = (i) => setPending((prev) => prev.filter((_, idx) => idx !== i));

  // Wait-state instrumentation (doc 179 Tier 0): a 1s tick drives the elapsed
  // clock + "still running" counters + stall detection while a turn streams.
  const [tickNow, setTickNow] = useState(0);
  const turnStartRef = useRef(null);          // when the user hit Send
  const lastFrameRef = useRef({ at: null, type: null }); // last SSE frame of ANY kind
  const firstFrameRef = useRef(false);        // has this turn received any frame yet
  const stallLoggedRef = useRef(false);       // one metric per stall episode

  useEffect(() => {
    if (!streaming) return undefined;
    setTickNow(Date.now());
    const t = setInterval(() => setTickNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [streaming]);

  // T0.7 — stall detector. The worker SSE stall (frame-silent 10+ min, observed
  // on prod 2026-06-10) would make a bare ticking clock actively misleading, so
  // any frame-silence beyond STALL_MS flips the status line and pulses Stop.
  const stalled = streaming && tickNow > 0 && lastFrameRef.current.at != null
    && tickNow - lastFrameRef.current.at > STALL_MS();

  useEffect(() => {
    if (!stalled || stallLoggedRef.current) return;
    stallLoggedRef.current = true;
    const detail = {
      conversation: credsRef.current.conversation || null,
      sinceLastFrameMs: tickNow - lastFrameRef.current.at,
      lastEventType: lastFrameRef.current.type,
      turnElapsedMs: turnStartRef.current ? tickNow - turnStartRef.current : null,
      at: new Date().toISOString(),
    };
    // The stall metric: this is the first instrumentation of the SSE-stall bug
    // we have only ever caught anecdotally — keep both sinks.
    console.warn('[hermes] SSE stall detected', detail);
    try { window.dispatchEvent(new CustomEvent('hermes:sse-stall', { detail })); } catch { /* metric only */ }
  }, [stalled, tickNow]);

  const credsRef = useRef({ baseUrl, apiKey, sessionKey, conversation });
  useEffect(() => {
    credsRef.current = { baseUrl, apiKey, sessionKey, conversation };
  }, [baseUrl, apiKey, sessionKey, conversation]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Replay the conversation transcript from Hermes' server-side store on open
  // (server-authoritative → loads on ANY device, survives sandbox recycles).
  // baseUrl=worker, apiKey=signed sessionToken. Empty/new/404 sessions → stay blank.
  const [historyLoading, setHistoryLoading] = useState(false);
  useEffect(() => {
    if (!conversation || !baseUrl || !apiKey) return;
    let cancelled = false;
    setHistoryLoading(true);
    getSessionMessages(baseUrl, apiKey, conversation)
      // Seed ONLY an empty chat. This effect re-fires on token refresh (focus /
      // near-expiry) — overwriting live state then would wipe client-side step
      // durations and in-flight turns (doc 179 bug: timers vanished on collapse).
      .then((rows) => { if (!cancelled && rows.length) setMessages((prev) => (prev.length ? prev : mapHermesMessages(rows))); })
      .catch(() => { /* new/empty session or asleep — render the empty ready state */ })
      .finally(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [conversation, baseUrl, apiKey]);

  const isRecoverableUpstreamError = (msg) => {
    const m = String(msg || '');
    return /Sandbox started\?|sandbox_not_ready|HTTP 50[234]|signed.*url|401|403/i.test(m);
  };

  // Merge a tool-activity frame into the last assistant message's activity log,
  // keyed by toolCallId (running → completed). "completed" frames omit
  // emoji/label, so we keep the prior values. startedAt is stamped on first
  // sight; durationMs on the completing frame (client-side ≈, see fmtDur).
  const applyTool = (ev) => setMessages((m) => {
    const last = m[m.length - 1];
    if (last?.role !== 'assistant') return m;
    const activity = last.activity ? [...last.activity] : [];
    const idx = activity.findIndex((a) => a.id === ev.id);
    const prev = idx >= 0 ? activity[idx] : {};
    const startedAt = prev.startedAt || Date.now();
    const status = ev.status || prev.status || 'running';
    const merged = {
      id: ev.id,
      tool: ev.tool || prev.tool,
      emoji: ev.emoji || prev.emoji || '',
      label: redactSecrets(ev.label) || prev.label || '',
      status,
      startedAt,
      durationMs: prev.durationMs
        ?? (status === 'completed' && prev.startedAt ? Date.now() - startedAt : undefined),
      result: prev.result || null,
    };
    if (idx >= 0) activity[idx] = merged; else activity.push(merged);
    return [...m.slice(0, -1), { ...last, activity }];
  });

  // Tier 1 (plan 179): the completions stream strips tool args/results, but the
  // session transcript carries both. Fetch it sparingly during a turn — debounced
  // on tool frames + once at turn end — and merge full commands + capped results
  // into the live trail by toolCallId. Same endpoint/auth as history replay, so
  // it rides the worker unchanged. Disable with localStorage.agentbro_enrich='off'.
  const enrichRef = useRef({ timer: null, inflight: false, again: false });
  const enrichTrail = async () => {
    const { baseUrl, apiKey, conversation } = credsRef.current;
    if (!baseUrl || !apiKey || !conversation) return;
    try { if (localStorage.getItem('agentbro_enrich') === 'off') return; } catch { /* default on */ }
    const st = enrichRef.current;
    if (st.inflight) { st.again = true; return; }
    st.inflight = true;
    try {
      const rows = await getSessionMessages(baseUrl, apiKey, conversation);
      const meta = extractToolMeta(rows);
      if (!meta.size) return;
      setMessages((m) => {
        const last = m[m.length - 1];
        if (last?.role !== 'assistant' || !last.activity?.length) return m;
        let changed = false;
        const activity = last.activity.map((a) => {
          const info = meta.get(a.id);
          if (!info) return a;
          const label = info.command || a.label;
          const result = a.result || info.result;
          if (label === a.label && result === a.result) return a;
          changed = true;
          return { ...a, label, result };
        });
        return changed ? [...m.slice(0, -1), { ...last, activity }] : m;
      });
    } catch { /* transcript momentarily unavailable — next tool frame retries */
    } finally {
      const st2 = enrichRef.current;
      st2.inflight = false;
      if (st2.again) { st2.again = false; scheduleEnrich(400); }
    }
  };
  const scheduleEnrich = (delay = 600) => {
    const st = enrichRef.current;
    if (st.timer) clearTimeout(st.timer);
    st.timer = setTimeout(() => { st.timer = null; enrichTrail(); }, delay);
  };
  useEffect(() => () => { if (enrichRef.current.timer) clearTimeout(enrichRef.current.timer); }, []);

  const streamOnce = async (wire, ctrl) => {
    const { baseUrl, apiKey, conversation, sessionKey } = credsRef.current;
    for await (const ev of streamChat({
      baseUrl, apiKey, conversation, sessionKey, messages: wire, signal: ctrl.signal,
    })) {
      // Every frame — content, tool, ping — is proof of life: reset the stall clock.
      lastFrameRef.current = { at: Date.now(), type: ev.type };
      firstFrameRef.current = true;
      stallLoggedRef.current = false;
      if (ev.type === 'content') {
        setMessages((m) => {
          const last = m[m.length - 1];
          if (last?.role !== 'assistant') return m;
          return [...m.slice(0, -1), { ...last, content: last.content + ev.text }];
        });
      } else if (ev.type === 'tool') {
        applyTool(ev);
        // args land in the transcript as the tool starts; results when it
        // completes — either way the next debounced fetch picks them up.
        scheduleEnrich(ev.status === 'completed' ? 400 : 700);
      }
    }
    scheduleEnrich(300); // final pass — catch results of the last steps
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if ((!text && pending.length === 0) || streaming || uploading) return;

    // Upload any attached files first; prepend their sandbox paths so the agent
    // knows where they landed (plan 206 Part B). Abort the send if upload fails.
    let note = '';
    if (pending.length) {
      setUploading(true);
      setUploadErr(null);
      try {
        const res = await uploadFiles(pending);
        const paths = (res.files || []).map((f) => f.sandbox_path);
        if (paths.length) {
          note = `[uploaded ${paths.length} file${paths.length > 1 ? 's' : ''}: ${paths.join(', ')}]\n\n`;
        }
      } catch (err) {
        setUploadErr(err.message || 'upload failed');
        setUploading(false);
        return;
      }
      setUploading(false);
      setPending([]);
    }

    setInput('');
    const content = note + text;
    const userMsg = { role: 'user', content };
    const assistantMsg = { role: 'assistant', content: '', activity: [] };
    setMessages((m) => [...m, userMsg, assistantMsg]);
    turnStartRef.current = Date.now();
    lastFrameRef.current = { at: Date.now(), type: null };
    firstFrameRef.current = false;
    stallLoggedRef.current = false;
    setStreaming(true);
    try { onUserMessage?.(content); } catch { /* ignore — UI side-effect only */ }

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    // Send ONLY the new turn — Hermes maintains the full thread server-side keyed by
    // X-Hermes-Session-Id (verified: continuity recalls prior context, no duplication).
    // Resending `messages` would double-count against the loaded/server history.
    const wire = [
      ...(seedContext ? [{ role: 'system', content: seedContext }] : []),
      userMsg,
    ];

    let attempts = 0;
    const maxAttempts = onSessionStale ? 2 : 1;
    while (attempts < maxAttempts) {
      attempts++;
      try {
        lastFrameRef.current = { at: Date.now(), type: null }; // stall clock measures this attempt
        await streamOnce(wire, ctrl);
        setStatusHint(null);
        break;
      } catch (err) {
        if (err.name === 'AbortError') { setStatusHint(null); break; }
        if (attempts < maxAttempts && isRecoverableUpstreamError(err.message)) {
          setMessages((m) => {
            const last = m[m.length - 1];
            if (last?.role !== 'assistant') return m;
            return [...m.slice(0, -1), { ...last, content: '', activity: [] }];
          });
          // T0.6: this retry path IS the broker wake (sandbox recycled/asleep —
          // onSessionStale re-brokers, which can cold-spawn for ~19s). Say so.
          setStatusHint(`Waking ${AGENT_NAME}…`);
          try {
            await onSessionStale();
            await new Promise((r) => setTimeout(r, 200));
          } catch (refreshErr) {
            setMessages((m) => {
              const last = m[m.length - 1];
              return [...m.slice(0, -1), { ...last, content: `\n\n_error: ${refreshErr.message}_` }];
            });
            setStatusHint(null);
            break;
          }
          continue;
        }
        setMessages((m) => {
          const last = m[m.length - 1];
          return [...m.slice(0, -1), { ...last, content: (last?.content || '') + `\n\n_error: ${err.message}_` }];
        });
        setStatusHint(null);
        break;
      }
    }
    setStreaming(false);
    abortRef.current = null;
  };

  const handleStop = () => abortRef.current?.abort();

  return (
    <div className="flex flex-col h-full bg-[#1a1c2a] text-gray-100">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.length === 0 && historyLoading && (
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Loader2 size={14} className="animate-spin" /> Loading conversation…
          </div>
        )}
        {messages.length === 0 && !historyLoading && (
          <div className="text-gray-500 text-sm leading-relaxed">
            {AGENT_NAME} is ready. Ask about analysts, predictions, screeners, charts.
            {seedContext && <div className="mt-2 text-xs text-gray-600 italic">Context loaded.</div>}
          </div>
        )}
        {messages.map((m, i) => {
          const isLast = i === messages.length - 1;
          const isStreamingThis = streaming && isLast && m.role === 'assistant';
          if (m.role === 'user') {
            return (
              <div key={i} className="text-sm text-blue-300">
                <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">user</div>
                <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
              </div>
            );
          }
          // Pull PDF citations out of the answer; render the prose with legacy
          // [SOURCE,…] tokens stripped (cleanedText), chips below (plan 206 Part A).
          const { citations, cleanedText } = m.content
            ? extractPdfCitations(m.content)
            : { citations: [], cleanedText: '' };
          return (
            <div key={i} className="text-sm text-gray-100">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">assistant</div>
              {m.activity?.length > 0 && (
                <ActivityTrail items={m.activity} done={!!m.content} now={isStreamingThis ? tickNow : 0} />
              )}
              {cleanedText && <MarkdownBody text={brand(cleanedText)} />}
              {isStreamingThis && (
                <StatusLine
                  activity={m.activity}
                  hasContent={!!m.content}
                  now={tickNow}
                  turnStartedAt={turnStartRef.current}
                  firstFrameSeen={firstFrameRef.current}
                  stalled={stalled}
                />
              )}
              {m.content && !isStreamingThis && <PdfCitations citations={citations} onOpen={onOpenPdf} />}
              {m.content && !isStreamingThis && <SharedFiles text={m.content} filesBase={filesBase} />}
              {m.content && !isStreamingThis && <LinkPreviews text={m.content} filesBase={filesBase} />}
            </div>
          );
        })}
        {statusHint && (
          <div className="text-[11px] text-amber-400/80 italic px-1">{statusHint}</div>
        )}
      </div>
      <form
        onSubmit={handleSend}
        onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
        className={`border-t border-gray-700/60 p-2 flex flex-col gap-1.5 ${dragOver ? 'bg-blue-500/10 ring-1 ring-inset ring-blue-500/40' : ''}`}
      >
        {uploadErr && <div className="text-[11px] text-amber-400 px-1">{uploadErr}</div>}
        {pending.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {pending.map((f, i) => (
              <span key={`${f.name}-${f.size}-${i}`}
                className="inline-flex items-center gap-1 bg-[#0f1018] border border-gray-700 rounded px-2 py-0.5 text-[11px] text-gray-300">
                📎 <span className="truncate max-w-[160px]" title={f.name}>{f.name}</span>
                <button type="button" onClick={() => removeFile(i)} className="text-gray-500 hover:text-gray-200" aria-label={`Remove ${f.name}`}>
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={UPLOAD_ACCEPT}
            className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={streaming || uploading}
            title="Attach files (PDF, Excel, Word, PowerPoint, image)"
            aria-label="Attach files"
            className="px-2 py-1.5 text-gray-400 hover:text-gray-200 disabled:opacity-40"
          >
            <Paperclip size={16} />
          </button>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onPaste={(e) => { if (e.clipboardData?.files?.length) addFiles(e.clipboardData.files); }}
            placeholder={placeholder}
            disabled={streaming}
            className="flex-1 bg-[#0f1018] border border-gray-700 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500 disabled:opacity-50"
          />
          {streaming ? (
            <button type="button" onClick={handleStop}
              className={`px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded text-sm font-medium ${
                stalled ? 'animate-pulse ring-2 ring-amber-400' : ''}`}>
              Stop
            </button>
          ) : (
            <button type="submit" disabled={uploading || (!input.trim() && pending.length === 0)}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded text-sm font-medium">
              {uploading ? 'Uploading…' : 'Send'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
